import "./ManageFeaturesModal.scss";

import React, { useEffect, useState } from "react";

import {
  Alert,
  Button,
  message,
  Modal,
  Space,
  Spin,
  Tag,
  Typography,
} from "antd";
import fileDownload from "js-file-download";
import PropTypes from "prop-types";

import { DownloadOutlined } from "@ant-design/icons";

import { useAppTheme } from "../../../context/ThemeProvider";
import { showAppError } from "../../../Utils/error";
import { getSingularOrPlural } from "../../../Utils/utilityFunctions";
import FeaturesAPI from "../../api/FeaturesAPI";
import {
  clearMangoAiCreditValue,
  deepCopyFeatureConfig,
  getAllInitialFeaturesConfig,
  hasAiVerseEdit,
  hasConfiguredFeatureValues,
  hasWithdrawalDelayEdit,
  isAiVerseEnabled,
  isDefaultHost,
  MANAGEABLE_FEATURES,
  processFeatureConfigWithDefaults,
  resolveFeatureSource,
  stripEmptyMangoAiCredit,
  stripUnwritableMangoAiFields,
  syncWithdrawalDelayWithInstantWithdrawal,
  validateExpiryRequirements,
} from "../manageFeaturesUtils";
import UploadUsersCSV from "./UploadUsersCSV";
import UsersTable from "./UsersTable";

const generateErrorCSV = (data) => {
  const invalidRows = data.filter((user) => user.status === "invalid");
  const headers = ["email", "host", "error"];
  const header = headers.join(",");

  const rows = invalidRows.map((row) => {
    const errorMsg = (row.errors || []).join("; ");
    const escaped =
      errorMsg.includes(",") || errorMsg.includes('"')
        ? `"${errorMsg.replace(/"/g, '""')}"`
        : errorMsg;
    return `${row.email || ""},${row.host || ""},${escaped}`;
  });

  return `${header}\n${rows.join("\n")}`;
};

const ManageFeaturesModal = ({
  open,
  onClose,
  onSuccess,
  preloadedUsers = null,
}) => {
  const { COLORS } = useAppTheme();

  // Step: "upload" | "validating" | "results"
  const [step, setStep] = useState("upload");
  const [csvData, setCsvData] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [isCsvUpload, setIsCsvUpload] = useState(false);
  const [isSameForAll, setIsSameForAll] = useState(true);
  const [validationError, setValidationError] = useState(null);

  useEffect(() => {
    if (preloadedUsers && preloadedUsers.length > 0) {
      const processedUsers = preloadedUsers.map((user, index) => {
        const config = user.featuresConfig
          ? JSON.parse(JSON.stringify(user.featuresConfig))
          : getAllInitialFeaturesConfig();
        const standardWithdrawalDelay =
          typeof user.standardWithdrawalDelay === "number"
            ? user.standardWithdrawalDelay
            : null;
        return {
          key: `${user.email}_${index}`,
          name: user.name,
          email: user.email,
          host: user.host || user.email.split("@")[0],
          userTier: user.userTier,
          whitelabelPlanType: user.whitelabelPlanType,
          status: "valid",
          // the AI credits input starts blank; the stored balance stays in
          // _originalFeaturesConfig as the placeholder and change baseline
          featuresConfig: clearMangoAiCreditValue(config),
          _originalFeaturesConfig: deepCopyFeatureConfig(config),
          standardWithdrawalDelay,
          _originalStandardWithdrawalDelay: standardWithdrawalDelay,
          // AI Verse lives in addOnsConfig, not featuresConfig
          aiVerse: isAiVerseEnabled(user),
          _originalAiVerse: isAiVerseEnabled(user),
        };
      });

      setCsvData(processedUsers);
      setIsCsvUpload(false);
      setStep("results");
    }
  }, [preloadedUsers]);

  const resetModal = () => {
    setStep("upload");
    setCsvData([]);
    setIsCsvUpload(false);
    setIsSameForAll(true);
    setIsSaving(false);
    setValidationError(null);
  };

  const handleCancel = () => {
    onClose();
    resetModal();
  };

  const handleCsvUpload = async (uploadedFile, entries) => {
    setValidationError(null);
    setStep("validating");

    try {
      const resp = await FeaturesAPI.validateManageFeatureEntries({ entries });

      if (resp?.code === 0 || resp?.code === 200) {
        const apiEntries = resp.result?.entries || resp.result || [];

        const processedData = apiEntries.map((entry, index) => {
          if (entry.isValid) {
            const enriched = entry.data || {};
            const config = {
              ...getAllInitialFeaturesConfig(),
              ...deepCopyFeatureConfig(enriched.featuresConfig || {}),
            };
            return {
              key: `${enriched.email}_${index}`,
              name: enriched.name || "",
              email: enriched.email || "",
              host: enriched.host || "",
              phone: enriched.phone || "",
              userTier: enriched.userTier || null,
              whitelabelPlanType: enriched.whitelabelPlanType || null,
              status: "valid",
              featuresConfig: clearMangoAiCreditValue(config),
              _originalFeaturesConfig: deepCopyFeatureConfig(config),
              // CSV validation does not return the host's withdrawal delay;
              // the input starts blank and is only sent if the admin sets it
              standardWithdrawalDelay: null,
              _originalStandardWithdrawalDelay: null,
              // ...nor, unless the endpoint sends addOnsConfig, the stored AI
              // Verse state. Null means "unknown", so the switch renders off
              // and is only sent once the admin actually flips it.
              aiVerse: enriched.addOnsConfig
                ? isAiVerseEnabled(enriched)
                : null,
              _originalAiVerse: enriched.addOnsConfig
                ? isAiVerseEnabled(enriched)
                : null,
            };
          }

          const errorData = entry.data || {};
          return {
            key: `${errorData.email || "invalid"}_${index}`,
            name: "",
            email: errorData.email || "",
            host: errorData.host || "",
            status: "invalid",
            errors: entry.errors || ["Validation failed"],
          };
        });

        setCsvData(processedData);
        setIsCsvUpload(true);
        setIsSameForAll(true);
        setStep("results");
      } else {
        throw new Error(resp?.message || "Validation failed");
      }
    } catch (error) {
      setValidationError(error.message || "Error validating entries");
      setStep("upload");
    }
  };

  const handleUploadNewFile = () => {
    resetModal();
  };

  const handleDataChange = (newData) => {
    setCsvData(newData);
  };

  const handleSameForAllChange = (checked) => {
    setIsSameForAll(checked);

    if (checked && csvData.length > 0) {
      const firstValidUser = csvData.find((user) => user.status === "valid");
      // the withdrawal delay is only configurable on non-default hosts, so it
      // syncs from the first host where it can actually be set
      const delaySourceUser = csvData.find(
        (user) => user.status === "valid" && !isDefaultHost(user.host)
      );
      if (firstValidUser?.featuresConfig) {
        const newData = csvData.map((user) => {
          if (user.status !== "valid") return user;

          if (isDefaultHost(user.host)) {
            // default hosts take only the features that stay editable for
            // them — storage, and the AI Verse group with its add-on flag
            return {
              ...user,
              featuresConfig: {
                ...deepCopyFeatureConfig(user.featuresConfig || {}),
                [MANAGEABLE_FEATURES.STORAGE_LIMIT]: deepCopyFeatureConfig(
                  firstValidUser.featuresConfig[
                    MANAGEABLE_FEATURES.STORAGE_LIMIT
                  ]
                ),
                [MANAGEABLE_FEATURES.MANGO_AI]: deepCopyFeatureConfig(
                  firstValidUser.featuresConfig[MANAGEABLE_FEATURES.MANGO_AI]
                ),
              },
              aiVerse: firstValidUser.aiVerse,
            };
          }

          return {
            ...user,
            featuresConfig: deepCopyFeatureConfig(
              firstValidUser.featuresConfig
            ),
            ...(delaySourceUser && {
              standardWithdrawalDelay: delaySourceUser.standardWithdrawalDelay,
            }),
            aiVerse: firstValidUser.aiVerse,
          };
        });
        // syncing the first user's config can turn Instant Withdrawal off on
        // other rows, which makes their delay non-editable again
        setCsvData(syncWithdrawalDelayWithInstantWithdrawal(newData));
      }
    }
  };

  const handleDownloadErrorCSV = () => {
    const csv = generateErrorCSV(csvData);
    fileDownload(csv, "feature_update_validation_errors.csv");
  };

  const handleSave = async () => {
    const validUsers = csvData.filter((user) => user.status === "valid");
    if (validUsers.length === 0) {
      message.error("No valid users to process");
      return;
    }

    const hasValidConfig = validUsers.some(
      (user) =>
        user.featuresConfig &&
        Object.values(user.featuresConfig).some(hasConfiguredFeatureValues)
    );

    const hasWithdrawalDelayChange = validUsers.some(hasWithdrawalDelayEdit);
    const hasAiVerseChange = validUsers.some(hasAiVerseEdit);

    if (!hasValidConfig && !hasWithdrawalDelayChange && !hasAiVerseChange) {
      message.error("Please configure at least one feature for the users");
      return;
    }

    // Validating storage configuration
    const invalidUser = validUsers.find(
      (user) =>
        user.userTier === "tier1" &&
        (!user.featuresConfig?.STORAGE_LIMIT?.storage ||
          user.featuresConfig?.STORAGE_LIMIT.storage < 20)
    );

    if (invalidUser) {
      message.error(
        `Storage limit must be at least 20GB for ${invalidUser.email}`
      );
      return;
    }

    setIsSaving(true);

    try {
      const resp = await FeaturesAPI.editFeatureFlagForUsers({
        users: validUsers.map((user) => {
          let finalFeaturesConfig = { ...user.featuresConfig };

          const originalUser = preloadedUsers?.find(
            (original) =>
              original.email === user.email && original.host === user.host
          );

          if (originalUser?.featuresConfig?.COMMUNITY) {
            finalFeaturesConfig.COMMUNITY =
              originalUser.featuresConfig.COMMUNITY;
          }

          finalFeaturesConfig =
            processFeatureConfigWithDefaults(finalFeaturesConfig);

          Object.keys(finalFeaturesConfig).forEach((featureKey) => {
            const currentConfig = finalFeaturesConfig[featureKey];
            if (!currentConfig) return;

            if (isCsvUpload) {
              currentConfig.source = "admin_override";
            } else {
              const originalConfig = originalUser?.featuresConfig?.[featureKey];
              currentConfig.source = resolveFeatureSource(
                featureKey,
                currentConfig,
                originalConfig
              );
            }
          });

          // MANGO_AI is merged field by field on the server, so whatever is
          // left in the object is written back. Drop the read-only monthly
          // counters and the pre-migration booleans, then drop aicreditValue
          // unless the admin entered a real top-up — it is an amount ADDED to
          // the balance, so echoing the stored value would double it.
          const mangoAiConfig =
            finalFeaturesConfig[MANAGEABLE_FEATURES.MANGO_AI];
          if (mangoAiConfig) {
            finalFeaturesConfig[MANAGEABLE_FEATURES.MANGO_AI] =
              stripEmptyMangoAiCredit(
                stripUnwritableMangoAiFields(mangoAiConfig)
              );
          }

          // only send the delay when the admin could edit it and actually
          // changed it, so an unchanged (or non-editable) row can never
          // overwrite the host's stored value
          const withdrawalDelayChanged = hasWithdrawalDelayEdit(user);

          // `aiVerse` sits beside email/host rather than inside
          // featuresConfig, and is omitted unless the admin moved the switch —
          // an absent key leaves the add-on exactly as it was
          return {
            email: user.email,
            host: user.host,
            featuresConfig: finalFeaturesConfig,
            ...(withdrawalDelayChanged && {
              standardWithdrawalDelay: user.standardWithdrawalDelay,
            }),
            ...(hasAiVerseEdit(user) && { aiVerse: user.aiVerse }),
          };
        }),
      });

      if (resp?.code === 0 || resp?.code === 200) {
        message.success(
          `Successfully updated features for ${
            validUsers.length
          } ${getSingularOrPlural(validUsers.length, "user")}`
        );
        onSuccess?.(validUsers.length);
        handleCancel();
      } else {
        showAppError(resp);
      }
    } catch (error) {
      showAppError(error);
    } finally {
      setIsSaving(false);
    }
  };

  const validUsersCount = csvData.filter(
    (user) => user.status === "valid"
  ).length;
  const invalidUsersCount = csvData.filter(
    (user) => user.status === "invalid"
  ).length;
  const hasInvalidEntries = invalidUsersCount > 0;
  const hasExpiryErrors = validateExpiryRequirements(csvData);

  const renderUploadStep = () => (
    <div>
      {validationError && (
        <Alert
          message="Validation Error"
          description={validationError}
          type="error"
          showIcon
          closable
          onClose={() => setValidationError(null)}
          style={{ marginBottom: 16 }}
        />
      )}
      <UploadUsersCSV
        onUpload={handleCsvUpload}
        onError={(errorMsg) => {
          setValidationError(errorMsg);
        }}
        disabled={step === "validating"}
      />
    </div>
  );

  const renderValidatingStep = () => (
    <div
      style={{
        padding: "60px 0",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Spin size="large" />
      <Typography.Text style={{ marginTop: 16, fontSize: 16 }}>
        Validating entries...
      </Typography.Text>
      <Typography.Text type="secondary" style={{ marginTop: 8 }}>
        Please do not close this window
      </Typography.Text>
    </div>
  );

  const renderResultsStep = () => (
    <div className="csv-upload-success">
      <div className="csv-success-header">
        <Typography.Title level={4}>
          {preloadedUsers
            ? `Selected Users (${validUsersCount})`
            : `Validation Results`}
        </Typography.Title>
        {!preloadedUsers && (
          <Button
            type="link"
            onClick={handleUploadNewFile}
            style={{ color: COLORS.INFO }}
          >
            Upload New File
          </Button>
        )}
      </div>

      {isCsvUpload && (
        <div style={{ marginBottom: 16 }}>
          <Space
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Space>
              <Tag
                color="success"
                style={{ fontSize: 14, padding: "4px 12px" }}
              >
                Valid: {validUsersCount}
              </Tag>
              {invalidUsersCount > 0 && (
                <Tag
                  color="error"
                  style={{ fontSize: 14, padding: "4px 12px" }}
                >
                  Invalid: {invalidUsersCount}
                </Tag>
              )}
            </Space>
            {hasInvalidEntries && (
              <Button
                icon={<DownloadOutlined />}
                onClick={handleDownloadErrorCSV}
                size="small"
              >
                Download Error CSV
              </Button>
            )}
          </Space>
        </div>
      )}

      {hasInvalidEntries && (
        <Alert
          message="Some entries have errors"
          description={
            <div>
              <p style={{ margin: 0, marginBottom: 8 }}>
                {invalidUsersCount} out of {csvData.length}{" "}
                {csvData.length === 1 ? "entry has" : "entries have"} validation
                errors. Please download the error CSV, fix the issues, and
                re-upload.
              </p>
              <p style={{ margin: 0 }}>
                <strong>Note:</strong> You cannot proceed until all entries are
                valid.
              </p>
            </div>
          }
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      {!hasInvalidEntries && isCsvUpload && (
        <Alert
          message="All entries validated successfully!"
          description={
            <div>
              <p style={{ margin: 0, marginBottom: 8 }}>
                All {validUsersCount}{" "}
                {validUsersCount === 1 ? "entry has" : "entries have"} been
                validated. The table below shows their current feature
                configurations.
              </p>
              <p style={{ margin: 0 }}>
                <strong>Review and modify</strong> the feature flags as needed,
                then click Save to apply the changes.
              </p>
            </div>
          }
          type="success"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      <UsersTable
        data={csvData}
        onDataChange={handleDataChange}
        isSameForAll={isSameForAll}
        onSameForAllChange={handleSameForAllChange}
        isPreloaded={!!preloadedUsers || isCsvUpload}
      />
    </div>
  );

  const renderContent = () => {
    if (preloadedUsers) {
      return renderResultsStep();
    }

    switch (step) {
      case "upload":
        return renderUploadStep();
      case "validating":
        return renderValidatingStep();
      case "results":
        return renderResultsStep();
      default:
        return renderUploadStep();
    }
  };

  const renderFooter = () => {
    if (step === "upload") {
      return [
        <Button key="cancel" onClick={handleCancel}>
          Cancel
        </Button>,
      ];
    }

    if (step === "validating") {
      return null;
    }

    if (step === "results" && hasInvalidEntries) {
      return [
        <Button key="upload-new" onClick={handleUploadNewFile}>
          Upload New File
        </Button>,
        <Button
          key="download-errors"
          icon={<DownloadOutlined />}
          onClick={handleDownloadErrorCSV}
        >
          Download Error CSV
        </Button>,
      ];
    }

    return [
      <Button key="cancel" onClick={handleCancel}>
        Cancel
      </Button>,
      <Button
        key="save"
        type="primary"
        loading={isSaving}
        disabled={validUsersCount === 0 || hasExpiryErrors}
        onClick={handleSave}
      >
        Save ({validUsersCount})
      </Button>,
    ];
  };

  return (
    <Modal
      title="Update features"
      visible={open}
      open={open}
      onCancel={handleCancel}
      width={step === "upload" ? 700 : "100vw"}
      className="manage-features-modal"
      centered={false}
      footer={renderFooter()}
      destroyOnClose
      maskClosable={step === "upload"}
    >
      <div className="modal-content-wrapper">{renderContent()}</div>
    </Modal>
  );
};

ManageFeaturesModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
  preloadedUsers: PropTypes.arrayOf(
    PropTypes.shape({
      _id: PropTypes.string,
      name: PropTypes.string,
      email: PropTypes.string,
      host: PropTypes.string,
      userTier: PropTypes.string,
      whitelabelPlanType: PropTypes.string,
      featuresConfig: PropTypes.shape({}),
      addOnsConfig: PropTypes.shape({}),
      standardWithdrawalDelay: PropTypes.number,
    })
  ),
};

export default ManageFeaturesModal;
